import { TableCell as MuiTableCell, TableRow } from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';
import type { SortValue } from '../model';
import type { CatalogMessageId } from '~/i18n';

export type PatientListColumnId =
    | 'patient'
    | 'workflow'
    | 'assignee'
    | 'datasets'
    | 'lastActivity';

/** Design track width in px; 'flex' absorbs the remaining table width. */
type TrackWidth = number | 'flex';

export type PatientListColumn = {
    id: PatientListColumnId;
    /** Header label message id; PatientListHeader is the single format site. */
    labelId: CatalogMessageId;
    /** Server-side sort key; only sortable columns carry one. */
    sortKey?: SortValue;
    fullWidth: TrackWidth;
    /** Compact-mode (panel open) track width; absent = column is dropped. */
    compactWidth?: TrackWidth;
    /** Right-aligned column (header and cells). */
    alignEnd?: boolean;
};

/**
 * Single source of truth for the list's columns: order, header labels, track
 * widths, and compact-mode behavior. `table-layout: fixed` makes the <col>
 * widths (not cell content) drive alignment, so the header and every row stay
 * in lockstep. The flexible Workflow column ('flex') absorbs the remaining
 * width. The patient ID is folded into the Patient cell (not a column) and the
 * Open affordance is a hover overlay (not a column). Compact (panel open)
 * keeps Patient and Workflow only; the dropped facts move into those two
 * cells. Patient keeps its width in both modes, so opening the panel leaves
 * the Workflow column where it was rather than moving it under the pointer.
 */
export const PATIENT_LIST_COLUMNS: readonly PatientListColumn[] = [
    {
        id: 'patient',
        labelId: 'patientList.column.patient',
        sortKey: 'name',
        fullWidth: 260,
        compactWidth: 220,
    },
    {
        id: 'workflow',
        labelId: 'patientList.column.workflow',
        sortKey: 'courseStage',
        fullWidth: 'flex',
        compactWidth: 'flex',
    },
    {
        id: 'assignee',
        labelId: 'patientList.column.assignee',
        fullWidth: 150,
    },
    {
        id: 'datasets',
        labelId: 'patientList.column.datasets',
        fullWidth: 190,
    },
    {
        id: 'lastActivity',
        labelId: 'patientList.column.lastActivity',
        sortKey: 'lastActivity',
        fullWidth: 150,
        alignEnd: true,
    },
];

const COMPACT_COLUMNS = PATIENT_LIST_COLUMNS.filter(
    (column) => column.compactWidth !== undefined
);

/** The columns the given mode renders, in table order. */
export function visiblePatientListColumns(
    compact: boolean
): readonly PatientListColumn[] {
    return compact ? COMPACT_COLUMNS : PATIENT_LIST_COLUMNS;
}

// Cell padding on the 8px grid; each <col> width folds this in so content equals the design track.
const CELL_PAD_X = 8; // theme.spacing(1)
const OUTER_PAD_LEFT = 21; // 24 minus the 3px selection bar
const OUTER_PAD_RIGHT = 24; // theme.spacing(3)

export const LIST_MIN_WIDTH_FULL = 1340;
/** The patient cell plus room for the stage strip's eight marks. */
export const LIST_MIN_WIDTH_COMPACT = 420;

function colWidth(
    track: TrackWidth | undefined,
    index: number,
    count: number
): number | undefined {
    if (typeof track !== 'number') {
        return undefined;
    }
    const left = index === 0 ? OUTER_PAD_LEFT : CELL_PAD_X;
    const right = index === count - 1 ? OUTER_PAD_RIGHT : CELL_PAD_X;
    return track + left + right;
}

/** <colgroup> pinning the fixed column widths; the 'flex' tracks share what is left. */
export function PatientListColgroup({ compact }: { compact: boolean }) {
    const columns = visiblePatientListColumns(compact);
    return (
        <colgroup>
            {columns.map((column, index) => {
                const track = compact ? column.compactWidth : column.fullWidth;
                const width = colWidth(track, index, columns.length);
                return (
                    <col
                        key={column.id}
                        style={width === undefined ? undefined : { width }}
                    />
                );
            })}
        </colgroup>
    );
}

const cellPaddingX = (theme: Theme) => ({
    padding: theme.spacing(0, 1),
    '&:first-of-type': { paddingLeft: OUTER_PAD_LEFT },
    '&:last-of-type': { paddingRight: theme.spacing(3) },
});

/** Column header cell (sticky via the Table's `stickyHeader`); the theme paints its band. */
export const PatientListHeadCell = styled(MuiTableCell, {
    shouldForwardProp: (prop) => prop !== 'alignEnd',
})<{ alignEnd?: boolean }>(({ theme, alignEnd }) => ({
    ...cellPaddingX(theme),
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    height: 36,
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
    textAlign: alignEnd ? 'right' : 'left',
}));

/**
 * Group-header row cell spanning the whole table. Distinguished from the
 * column header by weight and case, since both are sticky-adjacent and a
 * reader has to tell "what this column is" from "what these rows share".
 */
export const PatientListGroupCell = styled(MuiTableCell)(({ theme }) => ({
    ...cellPaddingX(theme),
    height: 26,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.sunken,
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
}));

/** Patient-row data cell; content fonts/colors are set by the cell children. */
export const PatientListBodyCell = styled(MuiTableCell, {
    shouldForwardProp: (prop) => prop !== 'alignEnd',
})<{ alignEnd?: boolean }>(({ theme, alignEnd }) => ({
    ...cellPaddingX(theme),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    verticalAlign: 'middle',
    borderBottom: `1px solid ${theme.palette.divider}`,
    textAlign: alignEnd ? 'right' : 'left',
}));

/** Height of a patient row, in px, in both column sets. */
const PATIENT_ROW_HEIGHT = 79;

/** Patient-row chassis: hover/selected paint plus the selected accent bar. */
export const PatientTableRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
    // Positioning context for the Open hover overlay.
    position: 'relative',
    // One height for both column sets, so opening the panel does not resize
    // every row. It is the taller of the two layouts: the compact row, whose
    // patient cell takes on the assignee as a third line. A table row's
    // height is a minimum, so content that genuinely needs more still gets it.
    height: PATIENT_ROW_HEIGHT,
    cursor: 'pointer',
    transition: 'background 100ms',
    backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
    '&:hover, &:focus-visible': {
        backgroundColor: isSelected
            ? theme.palette.action.selected
            : theme.palette.action.hoverNeutral,
    },
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '-2px',
    },
    // The overlay follows keyboard focus as well as hover, or it would be
    // unreachable without a pointer. Keyboard focus only: closing the panel
    // hands focus back to the row it was opened from, and a mouse close must
    // not leave an Open button standing on a row nobody is pointing at.
    '&:hover .patient-list-open, &:focus-visible .patient-list-open, & .patient-list-open:focus-within':
        {
            opacity: 1,
            pointerEvents: 'auto',
        },
    // The app theme zeroes the last row's cell border; the patient list keeps a
    // divider on every row. Doubled class beats the theme selector specificity.
    '&&:last-of-type > td': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    // 3px accent bar flush to the row's left edge; inset shadow keeps it
    // out of the layout so cell content never shifts.
    '& > td:first-of-type': {
        boxShadow: `inset 3px 0 0 ${
            isSelected ? theme.palette.primary.main : 'transparent'
        }`,
    },
}));
